/**
 * Manual feed registrations - feed handed out per pen in the farrowing rooms, which has no silo
 * behind it. Written from the mobile app; the dashboard only reads them.
 */
export interface FeedRegistration {
  id: string;
  farmId: string;
  locationId: string;
  locationNumber: string;
  /** Free text typed by the farmer - hand-fed feed has no registry entry to pick from. */
  feedName: string;
  amountGrams: number;
  /** The day the feed was handed out, `yyyy-MM-dd`. Not a timestamp. */
  date: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeedRegistrationsPage {
  items: FeedRegistration[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

export interface FeedRegistrationsQuery {
  /** Pens to report on. A room or a row is expressed as every pen id it contains. */
  locationIds: string[];
  /** `yyyy-MM-dd`, inclusive. */
  startDate?: string;
  /** `yyyy-MM-dd`, inclusive. */
  endDate?: string;
  /** Zero-based, as the MUI pagination gives it. The service translates to the API's 1-based page. */
  page: number;
  size: number;
}

export const FEED_PERIOD_OPTIONS = [7, 14, 30, 90];

/**
 * What span of feed to show.
 *
 * `all` is not "a very long period" - it omits the date filter entirely rather than picking an
 * arbitrary far-back start, so it cannot silently clip a pen that was fed before whatever bound we
 * would have invented.
 */
export type FeedPeriod =
  | { kind: 'all' }
  | { kind: 'days'; days: number }
  /** Both bounds inclusive, `yyyy-MM-dd`. Either may be empty while the user is still typing. */
  | { kind: 'custom'; startDate: string; endDate: string };

export const ALL_TIME_PERIOD: FeedPeriod = { kind: 'all' };

/**
 * The location views open on everything registered too: a pen fed weeks ago is still the answer to
 * "what has this pen eaten", and a default window silently hides it behind a picker nobody opens.
 */
export const DEFAULT_LOCATION_PERIOD: FeedPeriod = ALL_TIME_PERIOD;

/**
 * One animal's whole history is small - it lives a few months and only earns a share on days its pen
 * was fed - so there is nothing to gain by hiding most of it behind a default window.
 */
export const DEFAULT_ANIMAL_PERIOD: FeedPeriod = ALL_TIME_PERIOD;

/**
 * Resolves a period to the inclusive `yyyy-MM-dd` bounds the API filters on. An absent bound means
 * unbounded on that side; the service drops it from the query.
 */
export const feedPeriodRange = (
  period: FeedPeriod,
  today: Date = new Date()
): { startDate?: string; endDate?: string } => {
  if (period.kind === 'all') return {};

  if (period.kind === 'custom') {
    // A half-filled range still filters on the side that is filled, so the table narrows as the
    // user types rather than staying stale until both ends are valid.
    return { startDate: period.startDate || undefined, endDate: period.endDate || undefined };
  }

  const start = new Date(today);
  start.setDate(start.getDate() - (period.days - 1));

  return { startDate: toIsoDay(start), endDate: toIsoDay(today) };
};

/** Local calendar day, not `toISOString()`, which would shift the day in a timezone behind UTC. */
const toIsoDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** True when the day falls inside the period. Used where filtering has to happen client-side. */
export const feedPeriodCoversDay = (period: FeedPeriod, day: string, today: Date = new Date()): boolean => {
  const { startDate, endDate } = feedPeriodRange(period, today);

  if (startDate && day < startDate) return false;
  if (endDate && day > endDate) return false;

  return true;
};

/**
 * Amounts are stored in grams because that is what the mobile presets hand out (25 g … 1 kg), but a
 * room over a quarter reaches six digits. Switch to kg once grams stop being readable.
 */
export const formatFeedAmount = (amountGrams: number): string => {
  // Shares are fractional, so grams need rounding here; whole-pen amounts are integers and
  // format identically either way.
  if (amountGrams < 1000) return `${amountGrams.toLocaleString(undefined, { maximumFractionDigits: 2 })} g`;

  const kg = amountGrams / 1000;

  return `${kg.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kg`;
};

/**
 * `date` is a calendar day, not an instant. `new Date('2026-09-04')` parses as UTC midnight, so
 * formatting it in a farm timezone behind UTC would render the day before. Anchoring at midday puts
 * the instant far enough from both edges that no real farm timezone can shift the day.
 */
export const feedRegistrationDayInstant = (date: string): string => `${date}T12:00:00`;

/**
 * One pen feeding as it applies to a single animal: what the whole pen got, how many piglets it was
 * split across, and this animal's share of it.
 *
 * `allocatedGrams` is fractional - a pen amount rarely divides evenly by its piglet count - and is
 * derived on read rather than stored, so it changes as animals are fostered in and out of the pen.
 * `penAmountGrams` and `pigletCount` travel with it so the figure can be explained rather than
 * merely displayed.
 */
export interface AnimalFeedHistoryItem {
  /** The record the share came from. Not unique per row: one feeding can be split many ways. */
  sourceId: string;
  /** The supply mechanism that delivered it, e.g. `ManualLocation`. */
  source: string;
  /** The day the feed reached the pen, `yyyy-MM-dd`. Not a timestamp. */
  date: string;
  feedName: string;
  allocatedGrams: number;
  locationId: string;
  locationNumber: string;
  penAmountGrams: number;
  pigletCount: number;
}

export interface AnimalFeedHistory {
  animalId: string;
  items: AnimalFeedHistoryItem[];
  totalAllocatedGrams: number;
}

/** Everything registered of one feed on one day, across the requested pens. */
export interface FeedDailyTotal {
  /** `yyyy-MM-dd`. */
  date: string;
  feedName: string;
  amountGrams: number;
}

export interface FeedRegistrationAnimal {
  animalId: string;
  number: string;
  animalType: number;
}

/**
 * The animals one registration was split across - never the sows and gilts, who are fed on their
 * own curve. The split is even, so `allocatedGrams` is every listed animal's share.
 */
export interface FeedRegistrationAnimals {
  registrationId: string;
  date: string;
  locationId: string;
  amountGrams: number;
  allocatedGrams: number;
  animals: FeedRegistrationAnimal[];
}

/** One bar segment of a feed chart: an amount of one feed on one day. */
export interface FeedChartEntry {
  /** `yyyy-MM-dd`. */
  date: string;
  feedName: string;
  grams: number;
}

/**
 * Adds up the feed landing on each day and fills in the days between.
 *
 * The gaps matter. A pen is not fed every day, and the chart's day axis is categorical: with only the
 * fed days plotted, two feedings a fortnight apart would stand side by side as if on consecutive
 * days. Filling the quiet days with zero keeps the axis to scale. The range is bounded by the first
 * and last fed day, not by the period picked.
 */
export const buildDailyFeedTotals = (entries: FeedChartEntry[]): { day: string; grams: number }[] => {
  if (entries.length === 0) return [];

  const byDay = new Map<string, number>();
  for (const entry of entries) {
    // A pen can be fed more than once in a day, and an animal can be moved mid-day, so a day may
    // carry several entries.
    byDay.set(entry.date, (byDay.get(entry.date) ?? 0) + entry.grams);
  }

  const days = [...byDay.keys()].sort();
  const series: { day: string; grams: number }[] = [];

  // Iterated as UTC days purely to step the calendar; the values are date-only strings either side,
  // so no local offset is involved and no day can be skipped or repeated.
  const cursor = new Date(`${days[0]}T00:00:00Z`);
  const last = new Date(`${days[days.length - 1]}T00:00:00Z`);

  while (cursor <= last) {
    const day = cursor.toISOString().slice(0, 10);
    series.push({ day, grams: byDay.get(day) ?? 0 });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return series;
};
